import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { mockData } from '../mock';

const HeroSection = () => {
  const { hero } = mockData;

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${hero.backgroundImage})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/70 via-cyan-900/60 to-slate-900/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-teal-600/20 to-transparent"></div>
      </div>

      {/* Animated Water Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-gradient-to-r from-teal-400/30 to-cyan-400/30 blur-3xl animate-pulse"></div>
        <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-gradient-to-r from-cyan-400/30 to-blue-400/30 blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white/90 text-sm font-medium mb-6">
            <div className="w-2 h-2 bg-teal-400 rounded-full mr-2 animate-pulse"></div>
            Innovation Écologique
          </div>

          {/* Main Title */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-teal-200 to-cyan-200 bg-clip-text text-transparent">
              Good-Bloom
            </span>
            <br />
            <span className="text-3xl md:text-4xl font-light text-white/90">
              La dépollution aquatique
            </span>
            <br />
            <span className="text-3xl md:text-4xl font-light bg-gradient-to-r from-amber-200 to-orange-200 bg-clip-text text-transparent">
              par phytoremédiation et modules robotisés
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-4xl mx-auto leading-relaxed">
            {hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              onClick={() => scrollToSection('technologies')}
              size="lg"
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl group text-lg font-semibold"
            >
              {hero.ctaText}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              className="border-2 border-white/30 bg-white/10 backdrop-blur-md text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 group text-lg font-semibold"
            >
              <Play className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              Voir la démonstration
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="text-2xl font-bold text-teal-200 mb-2">Absorbe</div>
              <div className="text-white/80">nitrates, phosphate, métaux lourds et hydrocarbures</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-cyan-200 mb-2">3</div>
              <div className="text-white/80">Sites déployés</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-amber-200 mb-2">24/7</div>
              <div className="text-white/80">Monitoring continu</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;